import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Card, CardContent, CardActions,
    Button, Chip, Grid, MenuItem, Select, FormControl, InputLabel,
    InputAdornment, Paper, Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import { publicAPI } from '../../services/api';

const LEVEL_OPTIONS = [
    'artisan', 'qualifiant', 'technicien',
    'bac', 'bac+1', 'bac+2', 'bac+3', 'bac+4',
    'bac+5', 'bac+6', 'bac+7', 'bac+8',
];

export default function HomePage() {
    const [offers, setOffers] = useState([]);
    const [filieres, setFilieres] = useState([]);
    const [istas, setIstas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filiereFilter, setFiliereFilter] = useState('');
    const [levelFilter, setLevelFilter] = useState('');
    const [istaFilter, setIstaFilter] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [offersRes, filieresRes, istasRes] = await Promise.allSettled([
                publicAPI.getJobOffers(),
                publicAPI.getFilieres(),
                publicAPI.getIstas(),
            ]);
            if (offersRes.status === 'fulfilled') setOffers(offersRes.value.data);
            else console.error('Error loading offers:', offersRes.reason);
            if (filieresRes.status === 'fulfilled') setFilieres(filieresRes.value.data);
            else console.error('Error loading filieres:', filieresRes.reason);
            if (istasRes.status === 'fulfilled') setIstas(istasRes.value.data);
            else console.error('Error loading istas:', istasRes.reason);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredOffers = offers.filter(offer => {
        const matchSearch = !search ||
            offer.title.toLowerCase().includes(search.toLowerCase()) ||
            (offer.description && offer.description.toLowerCase().includes(search.toLowerCase()));
        const matchFiliere = !filiereFilter || (offer.filieres && offer.filieres.some(f => f.id === Number(filiereFilter)));
        const matchLevel = !levelFilter || (offer.required_levels && offer.required_levels.includes(levelFilter));
        const matchIsta = !istaFilter || (offer.ista && offer.ista.id === Number(istaFilter));
        return matchSearch && matchFiliere && matchLevel && matchIsta;
    });

    return (
        <Box>
            {/* ── Hero Section ──────────────────────────────────── */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #1b263b 0%, #415a77 100%)',
                borderRadius: 4, 
                p: { xs: 4, md: 6 }, 
                mb: 6, 
                color: 'white', 
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(27, 38, 59, 0.2)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -100,
                    right: -100,
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.05)',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -50,
                    left: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.05)',
                }
            }}>
                <Typography variant="h3" sx={{ 
                    fontWeight: 800, 
                    mb: 2, 
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.6rem', sm: '2.2rem', md: '3rem' },
                }}>
                    Offres de Vacation
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8, mb: 4, fontWeight: 400, maxWidth: 700, mx: 'auto', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
                    Découvrez les opportunités de vacations dans les ISTAs                
                </Typography>
                <Box sx={{ 
                    maxWidth: 700, 
                    mx: 'auto', 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    p: 1, 
                    borderRadius: 4,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                }}>
                    <TextField
                        fullWidth
                        placeholder="Rechercher par titre, module ou compétence..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            bgcolor: 'white', 
                            borderRadius: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: 'none' },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#1b263b', ml: 1 }} />
                                </InputAdornment>
                            ),
                            sx: { height: 56, fontSize: '1.1rem' }
                        }}
                    />
                </Box>
            </Paper>

            {/* ── Filters ───────────────────────────────────────── */}
            <Paper elevation={0} sx={{ 
                p: { xs: 2, sm: 3 }, 
                mb: 4, 
                borderRadius: { xs: 2, sm: 4 }, 
                bgcolor: 'white', 
                border: '1px solid rgba(0,0,0,0.05)',
                display: 'flex', 
                gap: { xs: 1.5, sm: 2 }, 
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                <Typography variant="subtitle2" sx={{ color: '#64748b', mr: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Filtres :
                </Typography>
                <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} size="small">
                    <InputLabel>ISTA</InputLabel>
                    <Select
                        value={istaFilter}
                        onChange={(e) => setIstaFilter(e.target.value)}
                        label="ISTA"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">Tous les ISTAs</MenuItem>
                        {istas.map(i => (
                            <MenuItem key={i.id} value={i.id}>{i.nom}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
                    <InputLabel>Filière</InputLabel>
                    <Select
                        value={filiereFilter}
                        onChange={(e) => setFiliereFilter(e.target.value)}
                        label="Filière"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">Toutes les filières</MenuItem>
                        {filieres.map(f => (
                            <MenuItem key={f.id} value={f.id}>{f.nom}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
                    <InputLabel>Niveau requis</InputLabel>
                    <Select
                        value={levelFilter}
                        onChange={(e) => setLevelFilter(e.target.value)}
                        label="Niveau requis"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">Tous les niveaux</MenuItem>
                        {LEVEL_OPTIONS.map(l => (
                            <MenuItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {(search || filiereFilter || levelFilter || istaFilter) && (
                    <Button
                        variant="text"
                        onClick={() => { setSearch(''); setFiliereFilter(''); setLevelFilter(''); setIstaFilter(''); }}
                        sx={{ textTransform: 'none', color: '#ef4444', fontWeight: 600 }}
                    >
                        Réinitialiser
                    </Button>
                )}
                <Typography variant="body2" sx={{ ml: { xs: 0, sm: 'auto' }, color: '#64748b', fontWeight: 500, width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'center', sm: 'right' } }}>
                    <Box component="span" sx={{ color: '#1b263b', fontWeight: 800 }}>{filteredOffers.length}</Box> opportunités disponibles
                </Typography>
            </Paper>

            {/* ── Offers Grid ───────────────────────────────────── */}
            {loading ? (
                <Grid container spacing={3}>
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <Skeleton variant="rounded" height={340} sx={{ borderRadius: 4 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : filteredOffers.length === 0 ? (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 6, bgcolor: 'white', border: '1px dashed #e2e8f0' }}>
                    <WorkIcon sx={{ fontSize: 80, color: '#e2e8f0', mb: 2 }} />
                    <Typography variant="h5" sx={{ color: '#1b263b', fontWeight: 700, mb: 1 }}>
                        Aucune offre ne correspond à vos critères
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Essayez de modifier vos filtres ou votre recherche pour voir plus d'opportunités.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={4}>
                    {filteredOffers.map(offer => (
                        <Grid item xs={12} sm={6} md={4} key={offer.id}>
                            <Card sx={{
                                width: '100%', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 4,
                                    background: 'linear-gradient(90deg, #1b263b 0%, #415a77 100%)',
                                }
                            }}>
                                <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {offer.ista && (
                                                <Chip
                                                    label={offer.ista.nom}
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: 'rgba(27, 38, 59, 0.05)', 
                                                        color: '#1b263b', 
                                                        fontWeight: 700,
                                                        borderRadius: 1.5,
                                                        fontSize: '0.7rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {offer.types?.map(t => (
                                                <Chip 
                                                    key={t} 
                                                    label={t} 
                                                    size="small" 
                                                    sx={{ 
                                                        bgcolor: '#415a77', 
                                                        color: 'white', 
                                                        fontWeight: 600,
                                                        borderRadius: 1.5,
                                                        fontSize: '0.7rem'
                                                    }} 
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                    
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 800, 
                                        mb: 1.5, 
                                        lineHeight: 1.3, 
                                        color: '#1b263b',
                                        display: '-webkit-box', 
                                        WebkitLineClamp: 2, 
                                        WebkitBoxOrient: 'vertical', 
                                        overflow: 'hidden',
                                        height: '3.4rem'
                                    }}>
                                        {offer.title}
                                    </Typography>

                                    <Typography variant="body2" sx={{
                                        color: '#64748b',
                                        mb: 3,
                                        display: '-webkit-box', 
                                        WebkitLineClamp: 3, 
                                        WebkitBoxOrient: 'vertical', 
                                        overflow: 'hidden',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.6
                                    }}>
                                        {offer.description || 'Spécialiste recherché pour animer des modules de formation technique...'}
                                    </Typography>
                                    
                                    <Box sx={{ mt: 'auto' }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                            {offer.filieres?.slice(0, 2).map(f => (
                                                <Chip 
                                                    key={f.id} 
                                                    label={f.code} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ 
                                                        borderRadius: 1, 
                                                        borderColor: '#e2e8f0',
                                                        color: '#415a77',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem'
                                                    }} 
                                                />
                                            ))}
                                            {offer.filieres?.length > 2 && (
                                                <Typography variant="caption" sx={{ alignSelf: 'center', color: '#94a3b8', fontWeight: 600 }}>
                                                    +{offer.filieres.length - 2} autres
                                                </Typography>
                                            )}
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ p: 0.5, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                                                        <SchoolIcon sx={{ fontSize: 16, color: '#415a77' }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                                                            Niveau
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1b263b' }}>
                                                            {offer.required_levels?.[0] || 'Bac+2'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ p: 0.5, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 16, color: '#415a77' }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                                                            Volume
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1b263b' }}>
                                                            {offer.total_hours}h total
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                                <Box sx={{ p: 3, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => navigate(`/offres/${offer.id}`)}
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 700,
                                            boxShadow: '0 4px 12px rgba(27, 38, 59, 0.15)',
                                        }}
                                    >
                                        Détails de l'offre
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>

    );
}
